<script lang="ts" context="module">
  export type EventResult = {
    id: string;
    cover: string | null;
    slug: string;
    name: string;
    description: string;
    price: string;
    is_complete: boolean; // has event finished?
    offer_replay_purchase: boolean; // does event offer a replay?
    duration_seconds: number;
    scheduled_at: string;
    seat_limit: number;
    seats_left: number;
    seller_id: string;
    seller_avatar: string | null;
    seller_name: string;
    seller_slug: string;
    game_logo: string;
    game: string;
    game_slug: string;
    game_id: string;
  };
</script>

<script lang="ts">
  import type { App } from '$lib/types';
  import { ProgressiveImage } from '$mds/Image';
  import { humanizeDurationHours, humanizeDurationMinutes } from '$lib/utils';
  import Tag from '$mds/Tag';
  import { faCamcorder, faCirclePlay, faPerson, faTicket } from '@fortawesome/pro-solid-svg-icons';
  import { dayjs } from '@metafy/dates';
  import { getContext } from 'svelte';
  import BlurImage from '$mds/Image/BlurImage.svelte';

  export let event: EventResult & { _formatted?: EventResult };

  let className = 'sm:hover:bg-ondark-low';
  export { className as class };

  const { irkAccount } = getContext<App>('app');

  $: durationShort = humanizeDurationMinutes(event?.duration_seconds / 60 || 1, { shorter: true, removeSpace: true });
  $: isRecording = event?.offer_replay_purchase && event?.is_complete;
  $: eventType = isRecording ? 'Event Replay' : 'Event';

  $: month = dayjs(event?.scheduled_at).tz($irkAccount?.timezone).format('MMM');
  $: day = dayjs(event?.scheduled_at).tz($irkAccount?.timezone).format('DD');
</script>

<a
  href="/events/{event?.slug}"
  data-sveltekit-preload-code="hover"
  data-sveltekit-preload-data="off"
  class="focus-visible:bg-ondark-low focus-visible:ring-ondark-focused group flex h-14 items-center justify-between space-x-3 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 sm:px-3 {className}"
  on:keydown
  on:click
  title="View event details"
>
  <div class="relative flex min-w-0 flex-1 items-center justify-start gap-2 pl-1">
    <div class="mask-to-r w-18 absolute -inset-1 rounded-l-md from-black to-transparent object-cover brightness-50">
      <BlurImage src={event?.cover || event.game_logo} class="h-full w-full object-cover" />
    </div>

    <div class="bg-ondark-medium z-1 flex flex-col items-center rounded px-2 pb-[2px] pt-1 sm:-ml-1">
      <span class="text-body-4xs {event.is_complete ? 'text-ondark-secondary/40' : ''}">{month}</span>
      <span class="text-display-xs {event.is_complete ? 'text-ondark-secondary/60' : 'text-ondark-primary'} text-sm"
        >{day}</span
      >
    </div>

    <div class="z-1 flex flex-col truncate">
      <h4 class="sm:divide-ondark-base flex items-end overflow-hidden sm:divide-x">
        <span class="highlight text-ondark-primary ml-1 inline-block truncate pr-2">
          {@html event._formatted?.name ?? event?.name ?? ''}
        </span>
        <span class="text-ondark-subtle text-body-2xs hidden pl-2 sm:inline-block">
          <span class="highlight text-ondark-subtle truncate">
            {@html event._formatted?.seller_name ?? event?.seller_name ?? ''}
          </span>
        </span>
      </h4>
      <h5 class="text-body-4xs text-ondark-secondary ml-2 inline-flex items-center gap-1 truncate">
        {#if event?.game}
          <span class="">
            <ProgressiveImage
              src={event?.game_logo}
              wrapClass=" ring-px ring-[#14141a] aspect-square size-3 sm:size-4 object-cover bg-ondark-low"
              sizes={{ xs: 48 }}
              alt="{event?.game} Logo"
              rounded="rounded-full"
            />
          </span>
        {/if}
        {event?.game ?? ''}
      </h5>
    </div>
  </div>
  <div class="flex min-w-0 items-center space-x-1.5">
    <Tag
      variant={isRecording || event.is_complete ? 'secondary' : event.seats_left < 10 ? 'alert' : 'positive'}
      title={isRecording ? 'This event has a replay' : `${event.seats_left} tickets remaining for this upcoming event`}
      icon={isRecording ? faCirclePlay : event.is_complete ? null : faTicket}
    >
      {isRecording ? 'Replay' : event.is_complete ? 'Ended' : event.seats_left}
    </Tag>

    <Tag variant="secondary" title="Event duration" class="hidden sm:inline-flex">
      {durationShort}
    </Tag>
  </div>
</a>
